import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Dialog, Typography } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";

import useToaster from "../../../hooks/useToaster";
import useTrendScout from "../../../hooks/useTrendScout";

import {
  BrowseTrigger,
  CardHeading,
  CardName,
  FlagFallback,
  FlagImg,
  InlineEmpty,
  ModalBody,
  ModalCount,
  ModalEmpty,
  ModalFooter,
  ModalHeader,
  ModalSearch,
  ModalTitle,
  ModalToolbar,
  SaveBar,
  SaveBarActions,
  SaveBarSummary,
  SectionActions,
  SectionHeader,
  SectionLabel,
  SectionLink,
  SelectableCard,
  SelectableCardCheck,
  SelectableGrid,
  SettingsCardWide,
  SettingsGrid,
  TopicSwatch,
} from "../style.jsx";
import TrendScoutSkeleton from "../skeleton.jsx";
import { topicColor } from "../topics";
import { flagFromCode, flagSvgUrl } from "../utils.js";

const arraysEqual = (a, b) => {
  if (a.length !== b.length) return false;
  const sa = new Set(a);
  for (const x of b) if (!sa.has(x)) return false;
  return true;
};

function FlagIcon({ code }) {
  const [error, setError] = useState(false);
  if (!code) return <FlagFallback>🌐</FlagFallback>;
  if (error) return <FlagFallback>{flagFromCode(code) || "🌐"}</FlagFallback>;
  return (
    <FlagImg
      src={flagSvgUrl(code)}
      alt={code}
      loading="lazy"
      onError={() => setError(true)}
    />
  );
}

function CountryCard({ country, selected, onToggle }) {
  return (
    <SelectableCard
      type="button"
      $selected={selected}
      onClick={() => onToggle(country.code)}
      aria-pressed={selected}
    >
      <SelectableCardCheck $selected={selected}>
        {selected && <CheckRoundedIcon />}
      </SelectableCardCheck>
      <FlagIcon code={country.code} />
      <CardName>{country.name || country.code}</CardName>
    </SelectableCard>
  );
}

function BrowseCountriesDialog({
  open,
  onClose,
  countries,
  selected,
  onToggle,
  onSelectAll,
  onClear,
  text,
}) {
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter(
      (c) =>
        c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)
    );
  }, [query, countries]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: { borderRadius: 3, overflow: "hidden" },
      }}
    >
      <ModalHeader>
        <ModalTitle>{text.coverage.modalTitle}</ModalTitle>
        <Typography variant="caption" color="text.secondary">
          {selected.length}/{countries.length}
        </Typography>
      </ModalHeader>
      <ModalToolbar>
        <ModalSearch
          autoFocus
          placeholder={text.coverage.searchPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <SectionLink type="button" onClick={onSelectAll}>
          {text.coverage.selectAll}
        </SectionLink>
        <SectionLink
          type="button"
          onClick={onClear}
          disabled={selected.length === 0}
        >
          {text.coverage.clear}
        </SectionLink>
      </ModalToolbar>
      <ModalBody>
        {filtered.length === 0 ? (
          <ModalEmpty>{text.coverage.noResults}</ModalEmpty>
        ) : (
          <SelectableGrid>
            {filtered.map((c) => (
              <CountryCard
                key={c.code}
                country={c}
                selected={selected.includes(c.code)}
                onToggle={onToggle}
              />
            ))}
          </SelectableGrid>
        )}
      </ModalBody>
      <ModalFooter>
        <ModalCount>
          {text.coverage.summaryCountries
            .replace("{c}", selected.length)
            .replace("{tc}", countries.length)}
        </ModalCount>
        <Button
          variant="contained"
          size="small"
          onClick={onClose}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            bgcolor: "text.primary",
            color: "background.default",
            "&:hover": { bgcolor: "text.primary", opacity: 0.9 },
          }}
        >
          {text.coverage.done}
        </Button>
      </ModalFooter>
    </Dialog>
  );
}

export default function SettingsTab({ config, loading, text, onSaved }) {
  const { addToaster } = useToaster();
  const { saveConfig } = useTrendScout();

  const availableCountries = config?.availableCountries || [];
  const availableTopics = config?.availableTopics || [];
  const baseSelectedCountries = config?.selectedCountries || [];
  const baseSelectedTopics = config?.selectedTopics || [];

  const [selCountries, setSelCountries] = useState(baseSelectedCountries);
  const [selTopics, setSelTopics] = useState(baseSelectedTopics);
  const [saving, setSaving] = useState(false);
  const [browseOpen, setBrowseOpen] = useState(false);

  useEffect(() => {
    setSelCountries(baseSelectedCountries);
    setSelTopics(baseSelectedTopics);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const dirty = useMemo(
    () =>
      !arraysEqual(selCountries, baseSelectedCountries) ||
      !arraysEqual(selTopics, baseSelectedTopics),
    [selCountries, selTopics, baseSelectedCountries, baseSelectedTopics]
  );

  if (loading) return <TrendScoutSkeleton rows={4} />;

  const toggleCountry = (code) =>
    setSelCountries((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  const toggleTopic = (topic) =>
    setSelTopics((prev) =>
      prev.includes(topic) ? prev.filter((t) => t !== topic) : [...prev, topic]
    );

  const selectAllCountries = () =>
    setSelCountries(availableCountries.map((c) => c.code));
  const clearCountries = () => setSelCountries([]);
  const selectAllTopics = () => setSelTopics([...availableTopics]);
  const clearTopics = () => setSelTopics([]);

  const discard = () => {
    setSelCountries(baseSelectedCountries);
    setSelTopics(baseSelectedTopics);
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await saveConfig(selCountries, selTopics);
      if (res?.ok) {
        addToaster({
          message: text.toaster.saveSuccess,
          severity: "success",
        });
        onSaved && onSaved();
      } else {
        addToaster({ message: text.toaster.saveError, severity: "error" });
      }
    } catch (_) {
      addToaster({ message: text.toaster.saveError, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const topicLabel = (t) => (text.topicNames && text.topicNames[t]) || t;

  // Resolve the catalog entries for currently selected countries (preserves
  // catalog order for visual stability).
  const selectedCountryCards = availableCountries.filter((c) =>
    selCountries.includes(c.code)
  );

  const summary = text.coverage.summary
    .replace("{c}", selCountries.length)
    .replace("{tc}", availableCountries.length)
    .replace("{t}", selTopics.length)
    .replace("{tt}", availableTopics.length);

  return (
    <SettingsGrid>
      <SettingsCardWide>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <CardHeading>{text.coverage.heading}</CardHeading>
          <Typography variant="caption" color="text.secondary">
            {text.coverage.help}
          </Typography>
        </Box>

        {/* Countries — selected inline + browse-all modal */}
        <Box>
          <SectionHeader>
            <SectionLabel>
              {text.coverage.countries} · {selCountries.length}/
              {availableCountries.length}
            </SectionLabel>
            <SectionActions>
              <SectionLink
                type="button"
                onClick={clearCountries}
                disabled={selCountries.length === 0}
              >
                {text.coverage.clear}
              </SectionLink>
            </SectionActions>
          </SectionHeader>

          {selectedCountryCards.length === 0 ? (
            <InlineEmpty>{text.coverage.noCountriesSelected}</InlineEmpty>
          ) : (
            <SelectableGrid>
              {selectedCountryCards.map((c) => (
                <CountryCard
                  key={c.code}
                  country={c}
                  selected={true}
                  onToggle={toggleCountry}
                />
              ))}
            </SelectableGrid>
          )}

          <Box sx={{ mt: 1.5 }}>
            <BrowseTrigger type="button" onClick={() => setBrowseOpen(true)}>
              <PublicRoundedIcon sx={{ fontSize: 18 }} />
              {text.coverage.browseCountries.replace(
                "{n}",
                availableCountries.length
              )}
              <AddRoundedIcon sx={{ fontSize: 16, marginInlineStart: "auto" }} />
            </BrowseTrigger>
          </Box>
        </Box>

        {/* Topics — all 8 inline */}
        <Box sx={{ mt: 2 }}>
          <SectionHeader>
            <SectionLabel>
              {text.coverage.topics} · {selTopics.length}/
              {availableTopics.length}
            </SectionLabel>
            <SectionActions>
              <SectionLink
                type="button"
                onClick={selectAllTopics}
                disabled={selTopics.length === availableTopics.length}
              >
                {text.coverage.selectAll}
              </SectionLink>
              <SectionLink
                type="button"
                onClick={clearTopics}
                disabled={selTopics.length === 0}
              >
                {text.coverage.clear}
              </SectionLink>
            </SectionActions>
          </SectionHeader>
          <SelectableGrid>
            {availableTopics.map((t) => {
              const selected = selTopics.includes(t);
              const tc = topicColor(t);
              return (
                <SelectableCard
                  key={t}
                  type="button"
                  $selected={selected}
                  onClick={() => toggleTopic(t)}
                  aria-pressed={selected}
                >
                  <SelectableCardCheck $selected={selected}>
                    {selected && <CheckRoundedIcon />}
                  </SelectableCardCheck>
                  <TopicSwatch $bg={tc.bg} $fg={tc.fg}>
                    {topicLabel(t).charAt(0).toUpperCase()}
                  </TopicSwatch>
                  <CardName>{topicLabel(t)}</CardName>
                </SelectableCard>
              );
            })}
          </SelectableGrid>
        </Box>

        {dirty && (
          <SaveBar>
            <SaveBarSummary>{summary}</SaveBarSummary>
            <SaveBarActions>
              <Button
                variant="text"
                size="small"
                onClick={discard}
                disabled={saving}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                {text.coverage.discard}
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={save}
                disabled={saving}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  bgcolor: "text.primary",
                  color: "background.default",
                  "&:hover": { bgcolor: "text.primary", opacity: 0.9 },
                }}
              >
                {saving ? text.coverage.saving : text.coverage.save}
              </Button>
            </SaveBarActions>
          </SaveBar>
        )}
      </SettingsCardWide>

      <BrowseCountriesDialog
        open={browseOpen}
        onClose={() => setBrowseOpen(false)}
        countries={availableCountries}
        selected={selCountries}
        onToggle={toggleCountry}
        onSelectAll={selectAllCountries}
        onClear={clearCountries}
        text={text}
      />
    </SettingsGrid>
  );
}
